import {
  ExerciseDataStyle,
  INRWorkoutSession,
  INRWorkoutSessionExercise,
  INRWorkoutSessionHit,
  INRWorkoutSessionRound,
} from 'interfaces/workout'

export const COMBINATION_HIT_COLORS = [
  '#00C0BA',
  '#C87868',
  '#008F8B',
  '#B59A5D',
  '#617B91',
  '#258FA3',
  '#5F9B8D',
  '#4A8392',
  '#697D9E',
  '#62B6A5',
]

export interface HitCombination {
  force: number
  hits: INRWorkoutSessionHit[]
  punchedAt: string
}

export interface DisplayForceHit extends INRWorkoutSessionHit {
  combination: boolean
  segments: INRWorkoutSessionHit[]
}

export const usesCombinationData = (exercise: INRWorkoutSessionExercise): boolean =>
  exercise.dataStyle === ExerciseDataStyle.COMBINATION_DATA

export const getCombinationWindow = (currentHitCount: number): number => {
  if (currentHitCount <= 1) return 1000
  if (currentHitCount === 2) return 900

  return Math.max(0, 850 - (currentHitCount - 3) * 30)
}

export const groupHitsIntoCombinations = (hits: INRWorkoutSessionHit[]): HitCombination[] => {
  const combinations: HitCombination[] = []

  hits.forEach((hit) => {
    const current = combinations[combinations.length - 1]

    if (current) {
      const previousHit = current.hits[current.hits.length - 1]
      const gap = Number(hit.punchedAt) - Number(previousHit.punchedAt)

      if (gap >= 0 && gap <= getCombinationWindow(current.hits.length)) {
        current.hits.push(hit)
        current.force += hit.force
        return
      }
    }

    combinations.push({
      force: hit.force,
      hits: [hit],
      punchedAt: hit.punchedAt,
    })
  })

  return combinations
}

export const getExerciseDisplayForceHits = (exercise: INRWorkoutSessionExercise): DisplayForceHit[] => {
  if (!usesCombinationData(exercise)) {
    return exercise.hits.map((hit) => ({
      ...hit,
      combination: false,
      segments: [hit],
    }))
  }

  return groupHitsIntoCombinations(exercise.hits).map((combination) => ({
    ...combination.hits[0],
    force: combination.force,
    combination: true,
    segments: combination.hits,
  }))
}

export const getRoundDisplayForceHits = (round: INRWorkoutSessionRound): DisplayForceHit[] =>
  round.exercises.flatMap(getExerciseDisplayForceHits)

export const getWorkoutDisplayForceHits = (workout: INRWorkoutSession): DisplayForceHit[] =>
  workout.rounds.flatMap(getRoundDisplayForceHits)

export const roundUsesCombinationData = (round: INRWorkoutSessionRound): boolean =>
  round.exercises.some(usesCombinationData)

export const workoutUsesCombinationData = (workout: INRWorkoutSession): boolean =>
  workout.rounds.some(roundUsesCombinationData)

export const getCombinationHitColor = (hitIndex: number): string =>
  COMBINATION_HIT_COLORS[hitIndex % COMBINATION_HIT_COLORS.length]
